import React, { useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import {
  makeStyles,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  IconButton,
} from "@material-ui/core";
import { green } from "@material-ui/core/colors";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { listQuickAnswerCategories } from "../../services/quickAnswerCategories";
import { getBackendUrl } from "../../config";

const useStyles = makeStyles((theme) => ({
  root: {
    flexWrap: "wrap",
  },
  textField: {
    marginRight: theme.spacing(1),
    width: "100%",
  },

  btnWrapper: {
    position: "relative",
  },

  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  textQuickAnswerContainer: {
    width: "100%",
  },
  mediaSection: {
    width: "100%",
    marginTop: theme.spacing(1),
  },
  mediaPreview: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: theme.spacing(1),
    border: "1px solid #ddd",
    borderRadius: 4,
    marginTop: 4,
  },
  mediaThumb: {
    width: 48,
    height: 48,
    objectFit: "cover",
    borderRadius: 4,
  },
  mediaName: {
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
}));

const QuickAnswerSchema = Yup.object().shape({
  shortcut: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
  message: Yup.string()
    .max(30000, "Too Long!")
    .nullable()
    .when("mediaPath", {
      is: (mediaPath) => !mediaPath,
      then: Yup.string().required("Agrega un texto o un archivo adjunto"),
      otherwise: Yup.string().nullable().notRequired(),
    }),
});

const prettySize = (bytes = 0) => {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const QuickAnswersModal = ({
  open,
  onClose,
  quickAnswerId,
  initialValues,
  onSave,
}) => {
  const classes = useStyles();
  const isMounted = useRef(true);
  const fileInputRef = useRef();

  const initialState = {
    shortcut: "",
    message: "",
    quickAnswerCategoryId: "",
    mediaPath: "",
    mediaMimetype: "",
    mediaOriginalName: "",
    mediaOrder: "media_first",
  };

  const [quickAnswer, setQuickAnswer] = useState(initialState);
  const [categories, setCategories] = useState([]);
  const [mediaUploading, setMediaUploading] = useState(false);
  const [uploadedFileSize, setUploadedFileSize] = useState(null);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    listQuickAnswerCategories()
      .then(setCategories)
      .catch(() => {});
  }, [open]);

  useEffect(() => {
    const fetchQuickAnswer = async () => {
      if (initialValues) {
        setQuickAnswer((prevState) => {
          return { ...prevState, ...initialValues };
        });
      }

      if (!quickAnswerId) return;

      try {
        const { data } = await api.get(`/quickAnswers/${quickAnswerId}`);
        if (isMounted.current) {
          setQuickAnswer(data);
        }
      } catch (err) {
        toastError(err);
      }
    };

    fetchQuickAnswer();
  }, [quickAnswerId, open, initialValues]);

  const handleClose = () => {
    onClose();
    setQuickAnswer(initialState);
    setUploadedFileSize(null);
  };

  const handleFileSelected = async (event, setFieldValue) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    const formData = new FormData();
    formData.append("media", file);

    setMediaUploading(true);
    try {
      const { data } = await api.post("/quickAnswers/media", formData);
      setFieldValue("mediaPath", data.mediaPath);
      setFieldValue("mediaMimetype", data.mediaMimetype);
      setFieldValue("mediaOriginalName", data.mediaOriginalName);
      setUploadedFileSize(file.size);
    } catch (err) {
      toastError(err);
    } finally {
      setMediaUploading(false);
    }
  };

  const handleRemoveMedia = (setFieldValue) => {
    setFieldValue("mediaPath", "");
    setFieldValue("mediaMimetype", "");
    setFieldValue("mediaOriginalName", "");
    setUploadedFileSize(null);
  };

  const handleSaveQuickAnswer = async (values) => {
    const payload = {
      ...values,
      quickAnswerCategoryId: values.quickAnswerCategoryId || null,
    };
    try {
      if (quickAnswerId) {
        await api.put(`/quickAnswers/${quickAnswerId}`, payload);
        handleClose();
      } else {
        const { data } = await api.post("/quickAnswers", payload);
        if (onSave) {
          onSave(data);
        }
        handleClose();
      }
      toast.success(i18n.t("quickAnswersModal.success"));
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        scroll="paper"
      >
        <DialogTitle id="form-dialog-title">
          {quickAnswerId
            ? `${i18n.t("quickAnswersModal.title.edit")}`
            : `${i18n.t("quickAnswersModal.title.add")}`}
        </DialogTitle>
        <Formik
          initialValues={quickAnswer}
          enableReinitialize={true}
          validationSchema={QuickAnswerSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveQuickAnswer(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ values, errors, touched, isSubmitting, setFieldValue }) => (
            <Form>
              <DialogContent dividers>
                <div className={classes.textQuickAnswerContainer}>
                  <Field
                    as={TextField}
                    label={i18n.t("quickAnswersModal.form.shortcut")}
                    name="shortcut"
                    autoFocus
                    error={touched.shortcut && Boolean(errors.shortcut)}
                    helperText={touched.shortcut && errors.shortcut}
                    variant="outlined"
                    margin="dense"
                    className={classes.textField}
                    fullWidth
                  />
                </div>
                <div className={classes.textQuickAnswerContainer}>
                  <FormControl
                    variant="outlined"
                    margin="dense"
                    className={classes.textField}
                  >
                    <InputLabel>Categoría</InputLabel>
                    <Select
                      label="Categoría"
                      value={values.quickAnswerCategoryId || ""}
                      onChange={(e) =>
                        setFieldValue("quickAnswerCategoryId", e.target.value)
                      }
                    >
                      <MenuItem value=""><em>Sin categoría</em></MenuItem>
                      {categories.map((cat) => (
                        <MenuItem key={cat.id} value={cat.id}>
                          {cat.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
                <div className={classes.textQuickAnswerContainer}>
                  <Field
                    as={TextField}
                    label={i18n.t("quickAnswersModal.form.message")}
                    name="message"
                    error={touched.message && Boolean(errors.message)}
                    helperText={touched.message && errors.message}
                    variant="outlined"
                    margin="dense"
                    className={classes.textField}
                    multiline
                    rows={5}
                    fullWidth
                  />
                </div>

                <div className={classes.mediaSection}>
                  <Typography variant="subtitle2">
                    Archivo adjunto (opcional)
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Audio, video, imagen o documento — se inserta junto al
                    atajo (ej. "/instructivo"), sin tener que buscarlo cada
                    vez. Los videos y audios se comprimen automáticamente al
                    subirlos, pero si el original pesa mucho (más de ~60-80
                    MB) la conversión puede fallar y mandarse sin comprimir —
                    WhatsApp puede rechazarlo. Para imágenes y documentos no
                    hay compresión: mantenlos livianos.
                  </Typography>

                  <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    onChange={(e) => handleFileSelected(e, setFieldValue)}
                  />

                  {values.mediaPath ? (
                    <div className={classes.mediaPreview}>
                      {values.mediaMimetype?.startsWith("image/") ? (
                        <img
                          alt={values.mediaOriginalName}
                          src={`${getBackendUrl()}/public/${values.mediaPath}`}
                          className={classes.mediaThumb}
                        />
                      ) : (
                        <InsertDriveFileIcon />
                      )}
                      <Typography className={classes.mediaName} variant="body2">
                        {values.mediaOriginalName}
                        {uploadedFileSize != null && (
                          <> &middot; {prettySize(uploadedFileSize)}</>
                        )}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveMedia(setFieldValue)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </div>
                  ) : (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AttachFileIcon />}
                      disabled={mediaUploading}
                      onClick={() => fileInputRef.current?.click()}
                      style={{ marginTop: 4 }}
                    >
                      {mediaUploading ? "Subiendo..." : "Adjuntar archivo"}
                    </Button>
                  )}

                  {values.mediaPath && (
                    <FormControl
                      variant="outlined"
                      className={classes.textField}
                      margin="dense"
                    >
                      <InputLabel>Orden de envío</InputLabel>
                      <Field
                        as={Select}
                        label="Orden de envío"
                        name="mediaOrder"
                      >
                        <MenuItem value="media_first">
                          Primero el archivo, luego el texto
                        </MenuItem>
                        <MenuItem value="text_first">
                          Primero el texto, luego el archivo
                        </MenuItem>
                      </Field>
                    </FormControl>
                  )}
                </div>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={isSubmitting}
                  variant="outlined"
                >
                  {i18n.t("quickAnswersModal.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  className={classes.btnWrapper}
                >
                  {quickAnswerId
                    ? `${i18n.t("quickAnswersModal.buttons.okEdit")}`
                    : `${i18n.t("quickAnswersModal.buttons.okAdd")}`}
                  {isSubmitting && (
                    <CircularProgress
                      size={24}
                      className={classes.buttonProgress}
                    />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default QuickAnswersModal;
